import {
  isRejectedWithValue,
  type Middleware,
  type MiddlewareAPI,
  type UnknownAction,
} from '@reduxjs/toolkit';
import { logOut } from '../../reducers/authorization-slice';
import { paths } from '../../routes/paths';

const AUTH_ENDPOINT_NAMES = new Set([
  'signIn',
  'signUp',
  'forgotPassword',
  'signInWithGoogle',
  'signUpWithGoogle',
]);

function isAuthEndpoint(action: UnknownAction): boolean {
  const meta = action.meta as
    | { arg?: { endpointName?: string; type?: string } }
    | undefined;
  const name = meta?.arg?.endpointName;
  return Boolean(name && AUTH_ENDPOINT_NAMES.has(name));
}

function redirectToSignIn(): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (window.location.pathname.startsWith('/auth')) {
    return;
  }
  window.location.assign(paths.signIn);
}

/**
 * Clears session when any RTK Query request returns HTTP 401
 * (except auth login/sign-up mutations).
 */
export const unauthorizedMiddleware: Middleware =
  (api: MiddlewareAPI) => (next) => (action) => {
    const result = next(action);

    if (!isRejectedWithValue(action) || isAuthEndpoint(action)) {
      return result;
    }

    const status = (action.payload as { status?: number | string } | undefined)
      ?.status;
    if (Number(status) !== 401) {
      return result;
    }

    const token = (
      api.getState() as { authorization?: { accessToken?: string | null } }
    ).authorization?.accessToken;

    if (!token) {
      return result;
    }

    api.dispatch(logOut());
    redirectToSignIn();
    return result;
  };
